<template>
    <ajax-form-modal v-model="isActive"
                     :hide-submit="!editFormType || !editFormType.action"
                     active-after-submit
                     :form-options="{action: editFormType ? editFormType.action : `/v1/athletes/${props.userKey}`, method: 'PUT', data: editFormType ? editFormType.data : null, notification: true}"
                     :modal-options="{title: `${$t('action.manage_profile')}: ${user ? user.display_name : null}`, noPadding: !editFormType, showCloseButton: !editFormType}" @success="handleUpdatedData">
        <fetch-loader v-if="pending || error" :pending="pending" :error="error" @retry="refresh" height="200"/>
        <template v-else>
            <transition :name="editFormType ? 'slide-transition' : 'slide-reverse-transition'" mode="out-in">
                <div v-if="!editFormType">
                    <card tile flat no-spacing @click="() => $refs.avatar.editAvatar()" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.avatar') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>
                        <card-text>
                            <profile-avatar ref="avatar" :model="user" passive editable round @updated="handleUpdatedAvatar"/>
                        </card-text>
                    </card>
                    <card tile flat no-spacing @click="editFormType = formTypes.base" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.base_data') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>
                        <card-text>
                            <description-list>
                                <description-list-item :title="$t('label.first_name')" :text="user.first_name"/>
                                <description-list-item :title="$t('label.last_name')" :text="user.last_name"/>
                                <description-list-item :title="$t('label.gender')" :text="user.gender === 'f' ? $t('label.female') : (user.gender === 'm' ? $t('label.male') : '-')"/>
                                <description-list-item :title="$t('label.birthday')" :text="user.birthday ? $date(user.birthday).format('LL') : '-'"/>
                                <description-list-item :title="$t('label.email')" :text="user.email ?? '-'"/>
                                <description-list-item :title="$t('label.starter_number')">
                                    <template #text>
                                        <chip v-if="user.starter_number" small>{{ user.starter_number }}</chip>
                                        <span v-else>-</span>
                                    </template>
                                </description-list-item>

                            </description-list>
                        </card-text>
                    </card>

                    <card tile flat no-spacing @click="editFormType = formTypes.contact" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.contact_data') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>
                        <card-text>
                            <description-list>
                                <description-list-item :title="$t('label.street')" :text="user.street ?? '-'"/>
                                <description-list-item :title="$t('label.city')" :text="user.postcode || user.city ? `${user.postcode} ${user.city}` : '-'"/>
                                <description-list-item :title="$t('label.country')">
                                    <template #text>
                                        <div v-if="user.country" class="flex items-center">
                                            <span>{{ user.country.locale_name }}</span>
                                            <avatar class="ml-8" :size="24" :src="user.country.flag"/>
                                        </div>
                                        <span v-else>-</span>
                                    </template>
                                </description-list-item>
                                <description-list-item :title="$t('label.phone')" :text="user.phone ?? '-'"/>
                                <description-list-item :title="$t('label.emergency_phone')" :text="user.emergency_phone ?? '-'"/>
                            </description-list>
                        </card-text>
                    </card>

                    <card tile flat no-spacing @click="editFormType = formTypes.club" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.main_club') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>
                        <card-text>
                            <profile-preview v-if="user.club" :model="user.club" :to="false" outlined/>
                            <span v-else>-</span>
                        </card-text>
                    </card>

                    <card tile flat no-spacing @click="editFormType = formTypes.description" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.profile_description') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>
                        <card-text>
                            <span class="whitespace-pre-wrap">{{ user.description ?? '-' }}</span>
                        </card-text>
                    </card>

                    <card tile flat no-spacing @click="editFormType = formTypes.sport_types" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.sports') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>

                        <card-text>
                            <template v-if="user.sportTypes?.length">
                                <chip v-for="sportType in user.sportTypes" :key="'sport-' + sportType.id" class="m-2">{{ sportType.title }}</chip>
                            </template>
                            <span v-else>-</span>
                        </card-text>
                    </card>

					<card v-if="$auth.user?.is_admin" tile flat no-spacing @click="editFormType = formTypes.connections" clickable class="divided">
						<card-title>
							<div class="w-full flex justify-between items-center">
								<span>{{ $t('label.connections') }}</span>
								<span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
							</div>
						</card-title>

						<card-text>
							<description-list>
								<description-list-item v-for="connection in user.connections" :key="connection.partner.value" :title="connection.partner.title" vertical-center>
									<template #prefix>
										<cdn-image :size="{width: 32}" :ratio="1" :src="connection.partner.image" class="mr-16"/>
									</template>
									<template #text>
								<span v-if="connection.enabled" class="flex items-center text-success">
									<icon :icon="icons.check" color="success" class="mr-8"/>
									<span>{{ $t('label.connected') }}</span>
								</span>
										<span v-else class="flex items-center text-gray-400">
									<icon :icon="icons.cancel" color="gray-400" class="mr-8"/>
									<span>{{ $t('label.not_connected') }}</span>
								</span>
									</template>
								</description-list-item>
							</description-list>
						</card-text>
					</card>

                    <card tile flat no-spacing @click="editFormType = formTypes.social" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.links') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>

                        <card-text>
                            <description-list>
                                <description-list-item :title="$t('label.website')" :text="user.socialMedia?.website ?? '-'"/>
                                <description-list-item :title="$t('label.instagram')" :text="user.socialMedia?.instagram ?? '-'"/>
                                <description-list-item :title="$t('label.facebook')" :text="user.socialMedia?.facebook ?? '-'"/>
                                <description-list-item :title="$t('label.twitter')" :text="user.socialMedia?.twitter ?? '-'"/>
                                <description-list-item :title="$t('label.youtube')" :text="user.socialMedia?.youtube ?? '-'"/>
                            </description-list>
                        </card-text>
                    </card>

                    <card tile flat no-spacing @click="editFormType = formTypes.password" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.password') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>

                        <card-text>
                            <alert :icon="false" :content="$t('view.my.profile.manage.password.description')"/>
                        </card-text>
                    </card>

                    <card tile flat no-spacing @click="editFormType = formTypes.settings" clickable class="divided">
                        <card-title>
                            <div class="w-full flex justify-between items-center">
                                <span>{{ $t('label.settings') }}</span>
                                <span class="text-sm font-regular font-text text-primary">{{ $t('action.edit') }}</span>
                            </div>
                        </card-title>

                        <card-text>
                            <alert :icon="false" :content="$t('view.my.profile.manage.settings.privacy.description')"/>
                        </card-text>
                    </card>
                </div>
                <user-base-data-form-inputs v-else-if="editFormType.key === formTypes.base.key" :data="editFormType.data" edit/>
                <user-contact-data-form-inputs v-else-if="editFormType.key === formTypes.contact.key" :data="editFormType.data" edit/>
                <user-club-data-form-inputs v-else-if="editFormType.key === formTypes.club.key" :data="editFormType.data" edit/>
                <user-description-data-form-inputs v-else-if="editFormType.key === formTypes.description.key" :data="editFormType.data" edit/>
                <user-sport-types-data-form-inputs v-else-if="editFormType.key === formTypes.sport_types.key" :data="editFormType.data" edit/>
                <user-connections-form-inputs v-else-if="editFormType.key === formTypes.connections.key" :user-key="user.id" @updated="refresh"/>
                <social-media-form-inputs v-else-if="editFormType.key === formTypes.social.key" :data="editFormType.data" edit/>
                <user-password-data-form-inputs v-else-if="editFormType.key === formTypes.password.key" :data="editFormType.data" edit/>
                <user-privacy-data-form-inputs v-else-if="editFormType.key === formTypes.settings.key" :data="editFormType.data" edit/>
            </transition>
        </template>

        <template #footer>
            <btn v-if="editFormType" @click="editFormType = null">
                <icon :icon="icons.back"/>
                <span>{{ $t('action.back') }}</span>
            </btn>
        </template>

    </ajax-form-modal>
</template>

<script setup lang="ts">

import {backIcon, checkIcon, cancelIcon} from "@spoferan/nuxt-spoferan/icons";

const route = useRoute();

const props = defineProps<{
	modelValue?: boolean,
	userKey: string;
}>();

const emit = defineEmits(['update:modelValue', 'success']);

const {isActive} = useToggleableComponent(props, emit);

const {reactiveData: user, pending, error, refresh} = await useApiFetch(`/v1/views/users/${props.userKey}/manage`, {
	auth: true
});

const editFormType = ref(null);
const formTypes = ref({});

watch(user, (newValue) => {
	if (newValue) {
		initFormTypes();

		// Re-open the connections form when being redirects from the connection oauth page
		if (route.query.connect_type) {
			editFormType.value = formTypes.value.connections;
		}
	}
}, {immediate: true});

const icons = {
	check: checkIcon,
	cancel: cancelIcon,
	back: backIcon,
}

function initFormTypes() {
	formTypes.value = {
		base: {
			key: 'base',
			action: `/v1/athletes/${props.userKey}`,
			data: {
				first_name: user.value.first_name,
				last_name: user.value.last_name,
				birthday: user.value.birthday,
				gender: user.value.gender,
				email: user.value.email,
				starter_number: user.value.starter_number
			},
			onSuccess: () => refresh()
		},
		contact: {
			key: 'contact',
			action: `/v1/athletes/${props.userKey}/contact`,
			data: {
				postcode: user.value.postcode,
				state_code: user.value.state_code,
				country_code: user.value.country_code,
				city: user.value.city,
				street: user.value.street,
				phone: user.value.phone,
				emergency_phone: user.value.emergency_phone,
				lat: user.value.lat,
				lng: user.value.lng,
			},
			onSuccess: () => refresh()
		},
		club: {
			key: 'club',
			action: `/v1/athletes/${props.userKey}`,
			data: {
				club_id: user.value.club_id
			},
			onSuccess: () => refresh()
		},
		description: {
			key: 'description',
			action: `/v1/athletes/${props.userKey}`,
			data: {
				description: user.value.description
			},
			onSuccess: () => refresh()
		},
		sport_types: {
			key: 'sport_types',
			action: `/v1/athletes/${props.userKey}/sport-types`,
			data: {
				sport_type_id: user.value.sportTypes?.length ? user.value.sportTypes.map(sportType => sportType._key) : []
			},
			onSuccess: () => refresh()
		},
		connections: {
			key: 'connections',
			action: null
		},
		social: {
			key: 'social',
			action: `/v1/athletes/${props.userKey}/social`,
			data: {...user.value.socialMedia},
			onSuccess: () => refresh()
		},
		password: {
			key: 'password',
			action: `/v1/athletes/${props.userKey}`,
			data: {
				current_password: null,
				password: null,
				password_confirmation: null
			},
		},
		settings: {
			key: 'settings',
			action: `/v1/athletes/${props.userKey}/settings`,
			data: {
				privacy_profile: user.value.privacy_profile,
				privacy_contact: user.value.privacy_contact,
				privacy_event_history: user.value.privacy_event_history,
				privacy_clubs: user.value.privacy_clubs
			},
			onSuccess: () => refresh()
		},
	}
}

function handleUpdatedData(data) {
	if (editFormType.value && editFormType.value.onSuccess) {
		editFormType.value.onSuccess(data);
	}
	editFormType.value = null;
	emit('success', user.value);
}

function handleUpdatedAvatar() {
	refresh();
	editFormType.value = null;
	emit('success', user.value);
}
</script>